import { useEffect, useState } from "react";
import { glanceApi, type EntryItem, type PartyGlance, type Preview, type VLedger, type VoucherReference , type CreditStatus } from "../lib/api";
import { fmtAmount } from "../lib/money";
import { gstinValid } from "../lib/india";
import type { Form } from "./entry";

type Check = { mark: "ok" | "info" | "warn" | "bad"; text: string };
const rupees = (n: number) => `₹ ${fmtAmount(n)}`;

// What would stop or trouble this entry, worked out as it is typed: shown beside "How it will post".
export function Checks({ f, party, preview, glance, glanceFor, items, ref, credit, alerts }: { f: Form; party?: VLedger; preview: Preview | null; glance: PartyGlance | null; glanceFor?: string | null;
  items: Map<string, EntryItem>; ref: VoucherReference; credit?: CreditStatus | null; alerts?: { level: "bad" | "warn" | "info"; text: string }[] }) {
  const out: Check[] = [];
  // what looks wrong, from the company's own history (sql/246)
  for (const a of alerts ?? []) out.push({ mark: a.level, text: a.text });
  if (credit?.limit != null) {
    const hold = credit.hold === "hold";
    out.push(credit.over ? { mark: hold ? "bad" : "warn", text: `Past the credit limit of ${fmtAmount(credit.limit)}: owes ${fmtAmount(credit.balance ?? 0)}${credit.orders ? ` plus ${fmtAmount(credit.orders)} on open orders` : ""}${hold ? "; posting needs an owner or admin" : ""}` }
      : { mark: "ok", text: `Within the credit limit: ${fmtAmount(credit.available ?? 0)} available` });
  }
  if (credit?.overdue_hold) out.push({ mark: credit.hold === "hold" ? "bad" : "warn", text: `A bill is ${credit.overdue_days} days overdue (hold after ${credit.overdue_limit})` });
  const invoice = ["sales", "purchase", "credit_note", "debit_note"].includes(f.base);
  const selling = f.base === "sales" || f.base === "debit_note";
  const who = selling || f.base === "credit_note" ? "Customer" : "Supplier";
  const problem = preview && "problem" in preview ? preview.problem : null;
  const worked = preview && !problem ? (preview as Exclude<Preview, { problem: string }>) : null;

  if (invoice && !party) out.push({ mark: "info", text: `Choose the ${who.toLowerCase()} to start` });
  if (invoice && party && f.gstOn) {
    if (party.gstin) out.push(gstinValid(party.gstin) ? { mark: "ok", text: `${who} GSTIN ${party.gstin} is well formed` }
                                                        : { mark: "bad", text: `${who} GSTIN ${party.gstin} fails its check digit: correct it on the account` });
    else if (party.state === "96") out.push(selling ? { mark: "info", text: "Overseas buyer: an export (zero-rated, under LUT unless tax is paid)" }
      : f.reverse_charge ? { mark: "info", text: "Overseas supplier, reverse charge: an import of services. You pay the IGST and claim it back (GSTR-3B 3.1(d) and 4A(2))" }
      : { mark: "warn", text: "Overseas supplier: for services, tick Reverse charge (you pay IGST on them). For goods leave it: IGST is paid at customs, on the bill of entry" });
    else out.push(selling ? { mark: "info", text: "No GSTIN: this is a supply to an unregistered buyer (B2C)" }
                          : { mark: "warn", text: "Supplier without a GSTIN: no input tax credit, so any GST on this bill stays in its cost. To claim it, choose the supplier with its GSTIN." });
  }
  if (worked?.gst && f.gstOn) {
    const home = ref.gst?.registrations.find((r) => r.id === worked.gst!.registration_id)?.state_code;
    const pos = ref.gst?.states.find((s) => s.code === worked.gst!.place_of_supply);
    out.push({ mark: "ok", text: `Place of supply ${pos ? `${pos.code} ${pos.name}` : worked.gst.place_of_supply}: ${worked.gst.place_of_supply === home ? "within the state, CGST and SGST" : "another state, IGST"}` });
  }
  if (selling) {
    for (const l of f.lines) {
      const it = l.item?.variant_id ? items.get(l.item.variant_id) : undefined;
      const q = Number(l.item?.qty || 0) * (it?.packs.find((p) => p.uom_id === l.item?.uom_id)?.factor ?? 1);
      if (!it || !q) continue;
      out.push(it.on_hand >= q ? { mark: "ok", text: `${it.sku}: enough stock (${it.on_hand.toLocaleString("en-IN")} ${it.unit}, need ${q.toLocaleString("en-IN")})` }
                               : { mark: "bad", text: `${it.sku}: only ${it.on_hand.toLocaleString("en-IN")} ${it.unit} in stock, need ${q.toLocaleString("en-IN")}` });
    }
  }
  if (worked?.document && f.base === "sales") {
    const d = worked.document;
    out.push({ mark: "info", text: d.kind === "bill_of_supply" ? `A bill of supply${d.declarations.some((x) => x.startsWith("Composition")) ? " (composition dealer: no GST is charged)" : " (nothing taxable on it)"}, numbered on its own series`
      : d.kind === "invoice_cum_bill" ? "An invoice-cum-bill of supply: taxable and exempt goods to an unregistered buyer (rule 46A)"
      : `A tax invoice${f.reverse_charge ? ": the buyer pays the tax under reverse charge, so it is shown but not charged"
          : f.eco_ledger && f.eco_section === "9_5" ? ": the e-commerce operator pays the tax under section 9(5), so it is shown but not charged"
          : f.eco_ledger ? ": sold through an e-commerce operator, which collects TCS on it (section 52)" : ""}` });
    for (const w of d.warnings) out.push({ mark: "warn", text: w });
  }
  if (glance?.einvoice && selling && f.gstOn && party?.gstin) out.push({ mark: "info", text: "E-invoice needed: get the IRN from the posted invoice" });
  if (glance && glance.overdue > 0 && (selling || f.base === "receipt"))
    out.push({ mark: "warn", text: `${glance.name} has ${rupees(glance.overdue)} overdue on ${glance.overdue_bills} bill${glance.overdue_bills === 1 ? "" : "s"}` });
  // money in or out from a party with open bills, not set against any: it stays on account and the bills still show unpaid
  if (glance && glance.balance > 0 && (f.base === "receipt" || f.base === "payment") && (glance.nature === "A") === (f.base === "receipt")) {
    const line = f.lines.find((l) => l.ledger_id === glanceFor && Number(l.amount) > 0);
    if (line && !line.bills?.length) out.push({ mark: "warn", text: `Not set against any of ${glance.name}'s bills: it stays on account and the ${f.base === "receipt" ? "invoices" : "bills"} still show unpaid. Choose Bills on the line to settle them (Oldest first does it in one go).` });
  }
  if (problem) out.push({ mark: "bad", text: problem.charAt(0).toUpperCase() + problem.slice(1) });
  else if (worked && Math.abs(worked.difference) < 0.005 && worked.lines.length) out.push({ mark: "ok", text: "Debits and credits agree" });
  if (!out.length) return null;
  return (
    <section className="card side-box">
      <h3>Checks before posting</h3>
      <ul className="checks">{out.map((c, i) => (
        <li key={i}><span className={`mark ${c.mark}`} aria-hidden="true">{c.mark === "ok" ? "✓" : c.mark === "info" ? "i" : "!"}</span>
          <span><span className="sr-only">{c.mark === "ok" ? "Passed: " : c.mark === "bad" ? "Problem: " : c.mark === "warn" ? "Note: " : ""}</span>{c.text}</span></li>))}</ul>
    </section>
  );
}

// the party at a glance: balance, overdue, how long they take to pay, the last invoice
export function useGlance(company: string, ledger: string | null, on: string) {
  const [g, setG] = useState<PartyGlance | null>(null);
  useEffect(() => {
    setG(null);
    if (!ledger) return;
    let live = true;
    glanceApi.party(company, ledger, on).then((x) => { if (live) setG(x); }).catch(() => {});
    return () => { live = false; };
  }, [company, ledger, on]);
  return g;
}

export function PartyPanel({ g }: { g: PartyGlance | null }) {
  if (!g) return null;
  const owesYou = g.nature === "A";
  return (
    <section className="card side-box">
      <h3>{g.name} at a glance</h3>
      <dl className="glance">
        <dt>{owesYou ? "Owes you" : "You owe"}</dt><dd>{rupees(Math.abs(g.balance))}{g.balance < 0 ? (owesYou ? " (in advance)" : " (advance paid)") : ""}</dd>
        <dt>Overdue</dt><dd className={g.overdue > 0 ? "bad" : ""}>{g.overdue > 0 ? `${rupees(g.overdue)} · ${g.overdue_bills} bill${g.overdue_bills === 1 ? "" : "s"}` : "Nothing"}</dd>
        <dt>{owesYou ? "Usually pays in" : "You usually pay in"}</dt><dd>{g.days_to_settle != null ? `${g.days_to_settle} days` : "Not enough history"}</dd>
        <dt>Last invoice</dt><dd>{g.last_invoice ? new Date(g.last_invoice + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "None yet"}</dd>
      </dl>
    </section>
  );
}
